// media-preview.component.ts
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core'; // Added OnChanges, SimpleChanges
import {DomSanitizer, SafeHtml, SafeResourceUrl} from '@angular/platform-browser'; // Import SafeHtml
import {NgClass} from '@angular/common';
import {ContentMessageType} from '../../../models/chat';
import DOMPurify from 'dompurify'; // Import DOMPurify
import {TranslateModule, TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-document-message',
  standalone: true,
  imports: [
    NgClass,
    TranslateModule,
  ],
  templateUrl: './document-message.component.html',
  styleUrl: './document-message.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentMessageComponent implements OnInit, OnChanges { // Implement OnChanges

  @Input() url: string = '';
  @Input() isTranslationEnabled: boolean = false; // New Input
  @Input() contentTranslated?: string; // New Input

  // Internal storage for normalized media type
  private _mediaType: ContentMessageType = ContentMessageType.Unsupported;

  // Use setter to normalize input
  @Input()
  set mediaType(value: ContentMessageType | string | null | undefined) {
    if (typeof value === 'string') {
      this._mediaType = this.convertMessageTypeStringToEnum(value);
    } else if (typeof value === 'number' && ContentMessageType[value] !== undefined) {
      // Check if it's a valid enum number
      this._mediaType = value;
    }
     else {
      // Default to Unsupported for null, undefined, or invalid numbers
      this._mediaType = ContentMessageType.Unsupported;
    }
    // Log the normalized type
    //console.log(`DocumentMessage normalized media type to: ${this._mediaType} (${ContentMessageType[this._mediaType]})`);
  }

  // Getter used internally and by the template
  get mediaType(): ContentMessageType {
    return this._mediaType;
  }

  // Getter for sanitized original text content (previously just safeUrl for text)
  get safeOriginalText(): SafeHtml {
    if (this.mediaType === ContentMessageType.Text) {
      return this.sanitizer.bypassSecurityTrustHtml(DOMPurify.sanitize(this.url || ''));
    }
    return ''; // Return empty or handle appropriately if needed elsewhere
  }

  // Getter for sanitized translated text content
  get safeTranslatedText(): SafeHtml | null {
    if (this.isTranslationEnabled && this.contentTranslated && this.mediaType === ContentMessageType.Text) {
      return this.sanitizer.bypassSecurityTrustHtml(DOMPurify.sanitize(this.contentTranslated));
    }
    return null;
  }

  safeUrl: SafeResourceUrl | string = ''; // Keep for non-text types
  ContentMessageType = ContentMessageType; // Expose enum to template
  showImagePlaceholder = false;

  constructor(
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.cdr.detectChanges()
    // Initial processing relies on the setter having run
    this.updateSafeUrl();
    //console.log(`DocumentMessage initialized. Final media type: ${this.mediaType} (${ContentMessageType[this.mediaType]})`);
  }

  // Update safeUrl when inputs change (URL or normalized mediaType)
  ngOnChanges(changes: SimpleChanges): void {
    // Check if url or mediaType (via setter) changed
    if (changes['url'] || changes['mediaType']) {
        this.updateSafeUrl();
        // Reset image error only if URL or type changes significantly
        if (changes['url'] || (changes['mediaType'] && changes['mediaType'].previousValue !== changes['mediaType'].currentValue)) {
             this.showImagePlaceholder = false;
        }
        //console.log(`DocumentMessage ngOnChanges detected changes. Final media type: ${this.mediaType} (${ContentMessageType[this.mediaType]})`);
    }
  }

  // Helper method to convert string message types to enum values
  private convertMessageTypeStringToEnum(type: string): ContentMessageType {
    const lowerType = type?.toLowerCase(); // Add safe navigation
    switch (lowerType) {
      case 'text': return ContentMessageType.Text;
      case 'image': return ContentMessageType.Image;
      case 'video': return ContentMessageType.Video;
      case 'audio': return ContentMessageType.Audio;
      case 'document': return ContentMessageType.Document;
      default:
        // console.warn(`Unsupported string message type received: ${type}`);
        return ContentMessageType.Unsupported;
    }
  }


  private updateSafeUrl(): void {
    // Use the internal _mediaType for checks
    if (this._mediaType !== ContentMessageType.Text && this.url) {
      try {
        //console.log(`Processing media type: ${this._mediaType} (${ContentMessageType[this._mediaType]}), URL: ${this.url}`);

        // Use bypassSecurityTrustResourceUrl for media types
        this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.url);
        //console.log('Sanitized URL:', this.safeUrl);

      } catch (e) {
        console.error("Error sanitizing URL:", this.url, e);
        this._mediaType = ContentMessageType.Unsupported; // Update internal state
        this.safeUrl = 'Invalid URL';
      }
    } else if (this._mediaType === ContentMessageType.Text) {
        // For text, safeUrl is not directly used for display anymore,
        // but we can keep it updated if needed elsewhere.
        // Display logic uses safeOriginalText and safeTranslatedText getters.
        this.safeUrl = this.url || '';
    }
     else {
      // Handle cases where URL is missing but type is not Text
      this.safeUrl = '';
      // console.warn(`Media type is ${ContentMessageType[this._mediaType]} but URL is missing.`);
    }
    // Manually trigger change detection if needed, although OnPush usually handles input changes
    // this.cdr.markForCheck();
  }

  // Extract file name - relies on URL parsing, may be inaccurate for masked/signed URLs
  get fileName(): string {
    if (!this.url || (this.mediaType !== ContentMessageType.Document && this.mediaType !== ContentMessageType.Unsupported)) {
        return ''; // No URL or not a file type we need a name for
    }
    try {
      const parsedUrl = new URL(this.url);
      const pathSegments = parsedUrl.pathname.split('/');
      // Decode URI component to handle encoded characters like %20
      const potentialName = decodeURIComponent(pathSegments[pathSegments.length - 1] || '');
      // Basic check if it looks like a filename (contains '.') or has some length
      // This is unreliable for URLs without filenames in the path (e.g., signed URLs)
      return potentialName && potentialName.includes('.') ? potentialName : (potentialName || 'File');
    } catch (e) {
      // Fallback for invalid URLs or if URL parsing fails
      const urlParts = this.url.split('/');
      const lastPart = urlParts[urlParts.length - 1]?.split('?')[0]; // Try to remove query params
      // Basic check if it looks like a file name
      return lastPart && lastPart.includes('.') ? decodeURIComponent(lastPart) : 'File';
    }
  }

  // Extract file extension - relies on fileName derived from URL, may be inaccurate
  get fileExtension(): string {
     const name = this.fileName; // Relies on the potentially inaccurate fileName
     if (!name || name === 'File') {
         // If fileName couldn't be determined reliably, use mediaType for a hint
         switch(this.mediaType) { // Use getter
             case ContentMessageType.Document: return 'DOC'; // Default assumption
             case ContentMessageType.Image: return 'IMG';
             case ContentMessageType.Video: return 'VID';
             case ContentMessageType.Audio: return 'AUD';
             default: return 'Documento';
         }
     }

     const parts = name.split('.');
     // Handle cases with no extension or hidden files starting with '.'
     if (parts.length > 1) {
         const ext = parts[parts.length - 1];
         // Basic length check and ensure it's alphanumeric
         return ext && ext.length < 10 && /^[a-zA-Z0-9]+$/.test(ext) ? ext.toUpperCase() : 'Documento';
     }
     return 'Documento'; // Fallback if no '.' found
  }

  // Get file size - No longer available from input, return empty string
  get fileSize(): string {
    // File size is unknown without API input
    return ''; // Return empty string as size is unavailable
  }

  /**
   * Get a user-friendly display name for the document
   */
  getDisplayName(): string {
    // If the fileName looks like a random ID (no extension, alphanumeric), make it more user-friendly
    if (this.fileName && /^[a-zA-Z0-9]+$/.test(this.fileName) && this.fileName.length > 8) {
      return this.translate.instant('agent.doc.document');
    }

    // If we have a reasonably meaningful filename, use it
    if (this.fileName && this.fileName !== 'File') {
      return this.fileName;
    }

    // Otherwise, create a descriptive name based on the type
    switch (this.mediaType) { // Use getter
      case ContentMessageType.Document:
        return this.translate.instant('agent.doc.document');
      default:
        return this.translate.instant('agent.doc.file');
    }
  }

  /**
   * Get a user-friendly display type
   */
  getDisplayType(): string {
    // If we have a proper extension, use it
    if (this.fileExtension &&
        this.fileExtension !== 'FILE' &&
        this.fileExtension !== 'DOC' &&
        this.fileExtension !== 'UNKNOWN') {
      return this.fileExtension;
    }

    // Otherwise determine type based on mediaType
    switch (this.mediaType) { // Use getter
      case ContentMessageType.Document:
        // Try to detect some common document types from the URL
        if (this.url.toLowerCase().includes('pdf')) return 'PDF';
        if (this.url.toLowerCase().includes('doc')) return 'DOC';
        if (this.url.toLowerCase().includes('xls')) return 'XLS';
        if (this.url.toLowerCase().includes('ppt')) return 'PPT';
        return this.translate.instant('agent.doc.document');
      default:
        return this.translate.instant('agent.doc.document');
    }
  }

  // Method to handle document/file click (download or open)
  handleDocumentClick(event: MouseEvent): void {
    // Prevent click propagation if needed, e.g., if inside another clickable element
    // event.stopPropagation();
    if (this.url && (this.mediaType === ContentMessageType.Document || this.mediaType === ContentMessageType.Unsupported)) { // Use getter
        // Open document/file URL in a new tab for download/viewing
        window.open(this.url, '_blank', 'noopener,noreferrer');
    }
    // Could add specific actions for other types if needed
  }

  // Helper to get specific icon class based on extension - using PrimeNG icons
  getIconClass(): string {
    switch (this.fileExtension) {
      case 'PDF': return 'pi-file-pdf';
      case 'DOC':
      case 'DOCX': return 'pi-file';  // PrimeNG doesn't have specific file type icons
      case 'PPT':
      case 'PPTX': return 'pi-file';
      case 'XLS':
      case 'XLSX': return 'pi-file';
      case 'ZIP':
      case 'RAR': return 'pi-file-export';
      case 'TXT': return 'pi-file';
      case 'CSV': return 'pi-file';
      // Add more specific icons as needed
      case 'IMG': return 'pi-image';
      case 'VID': return 'pi-video';
      case 'AUD': return 'pi-volume-up';
      default: return 'pi-file'; // Generic file icon
    }
  }

   // Method to handle image loading errors
   onImageError(event: Event): void {
     // console.warn("Failed to load image:", this.url);
     this.showImagePlaceholder = true;
     // Manually trigger change detection as the error might happen outside Angular zone
     this.cdr.detectChanges();

     // Optional: Hide the broken image element if placeholder is handled differently
     // const imgElement = event.target as HTMLImageElement;
     // if (imgElement) {
     //   imgElement.style.display = 'none';
     // }
   }
}
